import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import debounce from "lodash/debounce";

import { formatSuggestionAddress } from "../../lib/nationalIntakeRoute";

const SUGGESTION_DELAY_MS = 400;

export const renderAddressSuggestion = (suggestion) => (
  <span>{formatSuggestionAddress(suggestion)}</span>
);

// Shared behavior for the homepage and /verify address inputs. Rendering and
// navigation deliberately stay with each surface; this hook owns only the
// suggestion lifecycle and the state derived from it.
const useAddressAutosuggest = ({
  addressSuggestions = [],
  clearSuggestions,
  getAddressSuggestions,
  isActive = true,
  getExternalSuggestions,
  isLoading,
  selectAddress,
  suggestionsFailed = false,
}) => {
  const [address, setAddress] = useState("");
  const [suggestionStarted, setSuggestionStarted] = useState(true);
  const selectedSuggestionRef = useRef(null);
  const selectionClearedRef = useRef(false);
  const wrapperRef = useRef(null);
  const [, setPositionVersion] = useState(0);

  const callSuggestions = useMemo(
    () => debounce((query) => {
      getAddressSuggestions(query);
      setSuggestionStarted(false);
    }, SUGGESTION_DELAY_MS),
    [getAddressSuggestions],
  );

  useEffect(() => () => callSuggestions.cancel(), [callSuggestions]);

  // The dropdown is portaled to document.body and positioned against the
  // viewport. A lightweight rerender keeps it attached while the page moves.
  useEffect(() => {
    if (!isActive || !addressSuggestions.length) return undefined;
    const reposition = () => setPositionVersion((version) => version + 1);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [addressSuggestions.length, isActive]);

  const handleChange = useCallback((event, { newValue }) => {
    // One empty-selection dispatch per editing session is enough to invalidate
    // stale Redux state; subsequent keystrokes cannot make it any more empty.
    if (
      newValue !== selectedSuggestionRef.current &&
      !selectionClearedRef.current
    ) {
      selectAddress({});
      selectionClearedRef.current = true;
      selectedSuggestionRef.current = null;
    }
    setAddress(newValue);
  }, [selectAddress]);

  const onSuggestionFetchRequested = useCallback(({ value }) => {
    if (value === address) return;
    if (value.trim()) {
      setSuggestionStarted(true);
      callSuggestions(value);
      return;
    }
    callSuggestions.cancel();
    setSuggestionStarted(true);
    clearSuggestions();
  }, [address, callSuggestions, clearSuggestions]);

  const onSuggestionsClearRequested = useCallback(() => {
    callSuggestions.cancel();
    setSuggestionStarted(true);
    clearSuggestions();
  }, [callSuggestions, clearSuggestions]);

  const selectSuggestion = useCallback((suggestion) => {
    const formatted = formatSuggestionAddress(suggestion);
    selectedSuggestionRef.current = formatted;
    selectionClearedRef.current = false;
    selectAddress(suggestion);
    return formatted;
  }, [selectAddress]);

  const showEscapeHatch = addressSuggestions.length > 0 &&
    !addressSuggestions.some((suggestion) => suggestion.covered === false);

  const searchEveryState = useCallback((event) => {
    event.preventDefault();
    const query = address.trim();
    if (query) getExternalSuggestions(query);
  }, [address, getExternalSuggestions]);

  const getDropdownStyle = useCallback(() => {
    const input = wrapperRef.current &&
      wrapperRef.current.querySelector(".react-autosuggest__input");
    if (!input) return { position: "fixed", visibility: "hidden" };
    const rect = input.getBoundingClientRect();
    return {
      position: "fixed",
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width,
      zIndex: 4000,
    };
  }, []);

  const showAddressNotFound = Boolean(
    address.trim() &&
    addressSuggestions.length === 0 &&
    !isLoading &&
    !suggestionsFailed &&
    !suggestionStarted,
  );

  return {
    address,
    getDropdownStyle,
    handleChange,
    onSuggestionFetchRequested,
    onSuggestionsClearRequested,
    searchEveryState,
    selectSuggestion,
    showAddressNotFound,
    showEscapeHatch,
    wrapperRef,
  };
};

export default useAddressAutosuggest;
